// NEW CODE DEFINED ON 7 DEC 2025 
/* eslint-disable react/prop-types */


import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  Heading,
  Input,
  Text,
  Switch,
  Select,
  VStack,
  IconButton,
  Image,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionIcon,
  AccordionPanel,
  useToast,
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { FaRegImage } from "react-icons/fa6";
import { IoIosCloseCircle } from "react-icons/io";
import MultiSelect from "../../../components/MultiSelect";
import LabelInput from "../../../components/Input";
import DateTimeRange from "../../../components/DateTimeRange";
import {
  TWO_DECIMAL_FIELDS,
  clampToTwoDecimals,
  getProgressiveDecimalInput,
  getNumericInputProps,
  parseNumericInput,
  validateNumericRange,
} from "./decimalHelpers";
import { validateBasicVehicleFields } from "./validationSchema";

const PricingExtrasSection = ({
  sectionKey,
  pricingState,
  setPricingState,
  decimalValidationErrors,
  setDecimalValidationErrors,
  parseNumericInputWithValidation,
  pricingErrors,
  setPricingErrors,
  hasAnyDateTimeError,
  updatePricingWithValidation,
  getCurrentDate,
  getCurrentTime,
  handleDecimalBlur,
}) => {
  const emptyDateTimeErrors = {
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    general: "",
  };

  const handleToggle = (type, checked) => {
    if (!checked) {
      setPricingState((prev) => ({
        ...prev,
        [type]: {
          ...prev[type],
          ...(type === "discount" ? { isDiscountApply: false, discount: 0 } : {}),
          ...(type === "surcharge" ? { isSurchargeApply: false, amount: 0 } : {}),
          startDate: "",
          startTime: "",
          endDate: "",
          endTime: "",
        },
      }));

      setPricingErrors((prev) => ({
        ...prev,
        [sectionKey]: {
          ...prev[sectionKey],
          [type]: emptyDateTimeErrors,
        },
      }));

      setDecimalValidationErrors((prev) => {
        const updated = {};
        Object.entries(prev).forEach(([key, value]) => {
          if (key.startsWith(`${sectionKey}.${type}.`)) return;
          updated[key] = value;
        });
        return updated;
      });
      return;
    }

    setPricingState((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        ...(type === "discount" ? { isDiscountApply: true } : {}),
        ...(type === "surcharge" ? { isSurchargeApply: true } : {}),
        startDate: getCurrentDate(),
        startTime: getCurrentTime(),
        endDate: "",
        endTime: "",
      },
    }));
  };

  const premiumErrorKey = `${sectionKey}.premium.premiumSurcharge`;
  const discountErrorKey = `${sectionKey}.discount.discount`;
  const surchargeErrorKey = `${sectionKey}.surcharge.amount`;

  return (
    <Box mt={6}>
      <Flex align="center" gap={2} mb={2}>
        <Switch
          isChecked={pricingState.premium.isPremium}
          onChange={(e) => {
            const checked = e.target.checked;

            setPricingState((prev) => ({
              ...prev,
              premium: {
                ...prev.premium,
                isPremium: checked,
              },
            }));

            if (!checked) {
              setDecimalValidationErrors((prev) => {
                const updated = {};
                Object.entries(prev).forEach(([key, value]) => {
                  if (key.startsWith(`${sectionKey}.premium.`)) return;
                  updated[key] = value;
                });
                return updated;
              });
            }
          }}
        />
        <Text>Premium Service</Text>
      </Flex>

      {pricingState.premium.isPremium && (
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mt={3}>
          <Box>
            <LabelInput
              label="Premium Amount"
              type="number"
              value={pricingState.premium.premiumSurcharge ?? ""}
              min={0}
              step={0.01}
              onChange={(e) =>
                setPricingState((prev) => ({
                  ...prev,
                  premium: {
                    ...prev.premium,
                    premiumSurcharge: parseNumericInputWithValidation(
                      "premiumSurcharge",
                      e.target.value,
                      prev.premium?.premiumSurcharge ?? 0,
                      premiumErrorKey
                    ),
                  },
                }))
              }
              onBlur={(e) =>
                handleDecimalBlur(
                  "premiumSurcharge",
                  e.target.value,
                  premiumErrorKey,
                  (nextValue) =>
                    setPricingState((prev) => ({
                      ...prev,
                      premium: {
                        ...prev.premium,
                        premiumSurcharge: nextValue,
                      },
                    }))
                )
              }
            />
            {decimalValidationErrors[premiumErrorKey] && (
              <Text color="red.500" fontSize="xs" mt={1}>
                {decimalValidationErrors[premiumErrorKey]}
              </Text>
            )}
          </Box>

          <LabelInput
            label="Premium Addons"
            value={pricingState.premium.addons.join(",")}
            onChange={(e) =>
              setPricingState((prev) => ({
                ...prev,
                premium: {
                  ...prev.premium,
                  addons: e.target.value
                    .split(",")
                    .map((addon) => addon.trim())
                    .filter(Boolean),
                },
              }))
            }
          />
        </Grid>
      )}

      <Flex align="center" gap={2} mt={4}>
        <Switch
          isChecked={pricingState.discount.isDiscountApply}
          onChange={(e) => handleToggle("discount", e.target.checked)}
        />
        <Text>Apply Discount</Text>
      </Flex>

      {pricingState.discount.isDiscountApply && (
        <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={3}>
          <Box>
            <LabelInput
              label="Discount (%)"
              type="number"
              value={pricingState.discount.discount ?? ""}
              min={0}
              max={100}
              step={0.01}
              onChange={(e) =>
                setPricingState((prev) => ({
                  ...prev,
                  discount: {
                    ...prev.discount,
                    discount: parseNumericInputWithValidation(
                      "discount",
                      e.target.value,
                      prev.discount?.discount ?? 0,
                      discountErrorKey
                    ),
                  },
                }))
              }
              onBlur={(e) =>
                handleDecimalBlur(
                  "discount",
                  e.target.value,
                  discountErrorKey,
                  (nextValue) =>
                    setPricingState((prev) => ({
                      ...prev,
                      discount: {
                        ...prev.discount,
                        discount: nextValue,
                      },
                    }))
                )
              }
            />
            {decimalValidationErrors[discountErrorKey] && (
              <Text color="red.500" fontSize="xs" mt={1}>
                {decimalValidationErrors[discountErrorKey]}
              </Text>
            )}
          </Box>

          <DateTimeRange
            value={pricingState.discount}
            errors={pricingErrors[sectionKey].discount}
            isInvalid={hasAnyDateTimeError(pricingErrors[sectionKey].discount)}
            onChange={(field, value) =>
              updatePricingWithValidation(setPricingState, sectionKey, "discount", field, value)
            }
          />
        </Grid>
      )}

      <Flex align="center" gap={2} mt={4}>
        <Switch
          isChecked={pricingState.surcharge.isSurchargeApply}
          onChange={(e) => handleToggle("surcharge", e.target.checked)}
        />
        <Text>Apply Surcharge</Text>
      </Flex>

      {pricingState.surcharge.isSurchargeApply && (
        <Grid templateColumns="repeat(2, 1fr)" gap={4} mt={3}>
          <Box>
            <LabelInput
              label="Surcharge Amount (INR)"
              type="number"
              value={pricingState.surcharge.amount ?? ""}
              min={0}
              step={0.01}
              onChange={(e) =>
                setPricingState((prev) => ({
                  ...prev,
                  surcharge: {
                    ...prev.surcharge,
                    amount: parseNumericInputWithValidation(
                      "amount",
                      e.target.value,
                      prev.surcharge?.amount ?? 0,
                      surchargeErrorKey
                    ),
                  },
                }))
              }
              onBlur={(e) =>
                handleDecimalBlur(
                  "amount",
                  e.target.value,
                  surchargeErrorKey,
                  (nextValue) =>
                    setPricingState((prev) => ({
                      ...prev,
                      surcharge: {
                        ...prev.surcharge,
                        amount: nextValue,
                      },
                    }))
                )
              }
            />
            {decimalValidationErrors[surchargeErrorKey] && (
              <Text color="red.500" fontSize="xs" mt={1}>
                {decimalValidationErrors[surchargeErrorKey]}
              </Text>
            )}
          </Box>

          <DateTimeRange
            value={pricingState.surcharge}
            errors={pricingErrors[sectionKey].surcharge}
            isInvalid={hasAnyDateTimeError(pricingErrors[sectionKey].surcharge)}
            onChange={(field, value) =>
              updatePricingWithValidation(setPricingState, sectionKey, "surcharge", field, value)
            }
          />
        </Grid>
      )}
    </Box>
  );
};

const Form = ({ data, setData, handleSubmit, formRef, onCancel, isEdit = false }) => {
  const toast = useToast();
  const createEmptyDateTimeErrors = () => ({
    startDate: "",
    startTime: "",
    endDate: "",
    endTime: "",
    general: "",
  });

  const createDefaultLocalRental = () => ({
    durationHours: 0,
    includedKm: 0,
    price: 0,
    extraKmRate: 0,
    extraHourRate: 0,
    commissionPercent: 0,
  });

  const createDefaultLocalRentalGlobal = () => ({
    driverCharge: 0,
    nightCharge: 0,
    premium: {
      isPremium: false,
      premiumSurcharge: 0,
      addons: [],
    },
    discount: {
      isDiscountApply: false,
      discount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
    surcharge: {
      isSurchargeApply: false,
      amount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  });

  const [preview, setPreview] = useState(null);
  const [errors, setErrors] = useState({});
  const [decimalValidationErrors, setDecimalValidationErrors] = useState({});
  const COMMISSION_FIELDS = new Set(["commissionPercent"]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  const fuelTypes = ["Electric", "Diesel", "Petrol", "CNG"];
  const [localRentals, setLocalRentals] = useState([createDefaultLocalRental()]);
  const [localRentalGlobal, setLocalRentalGlobal] = useState(createDefaultLocalRentalGlobal());
  const [localRentalIncludes, setLocalRentalIncludes] = useState([]);
  const [localRentalExcludes, setLocalRentalExcludes] = useState([]);
  const [localRentalPricingErrors, setLocalRentalPricingErrors] = useState({
    discount: createEmptyDateTimeErrors(),
    surcharge: createEmptyDateTimeErrors(),
  });
  const [pricingErrors, setPricingErrors] = useState({
    outstationOneWay: {
      discount: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "", // optional fallback
      },
      surcharge: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "",
      }
    },
    outstationRoundTrip: {
      discount: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "",
      },
      surcharge: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "",
      }
    },
    airportTransfer: {
      discount: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "",
      },
      surcharge: {
        startDate: "",
        startTime: "",
        endDate: "",
        endTime: "",
        general: "",
      }
    },
  });


  const [outStationOneWay, setOutStationOneWay] = useState({
    // baseFare: 0,
    perKmRate: 0,
    extraKmRate: 0,
    driverCharge: 0,
    waitingCharge: 0,
    nightCharge: 0,
    tollCharge: 0,
    minimumKmPerDay: 0,
    commissionPercent: 0,
    premium: {
      isPremium: false,
      premiumSurcharge: 0,
      addons: [],
    },
    discount: {
      isDiscountApply: false,
      discount: 0,    // percentage
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
    surcharge: {
      isSurchargeApply: false,
      amount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  });

  const [outStationRoundTrip, setOutStationRoundTrip] = useState({
    // baseFare: 0,
    perKmRate: 0,
    extraKmRate: 0,
    driverCharge: 0,
    waitingCharge: 0,
    nightCharge: 0,
    tollCharge: 0,
    minimumKmPerDay: 0,
    commissionPercent: 0,
    premium: {
      isPremium: false,
      premiumSurcharge: 0,
      addons: [],
    },
    discount: {
      isDiscountApply: false,
      discount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
    surcharge: {
      isSurchargeApply: false,
      amount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  });

  const [airportTransfer, setAirportTransfer] = useState({
    // baseFare: 0,
    perKmRate: 0,
    extraKmRate: 0,
    airportSurcharge: 0,
    driverCharge: 0,
    waitingCharge: 0,
    nightCharge: 0,
    tollCharge: 0,
    minimumKmPerDay: 0,
    commissionPercent: 0,
    premium: {
      isPremium: false,
      premiumSurcharge: 0,
      addons: [],
    },
    discount: {
      isDiscountApply: false,
      discount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
    surcharge: {
      isSurchargeApply: false,
      amount: 0,
      startDate: null,
      endDate: null,
      startTime: null,
      endTime: null,
    },
  });

  const pricingOptions = [
    "Fuel",
    "Driver",
    "Toll",
    "State Tax",
    "Parking",
    "Entry Fees",
    "Any permissions or fees related to trip",
  ];

  const [outstationIncludes, setOutstationIncludes] = useState([]);
  const [outstationExcludes, setOutstationExcludes] = useState([]);

  const [outstationWithStopsIncludes, setOutstationWithStopsIncludes] = useState([]);
  const [outstationWithStopsExcludes, setOutstationWithStopsExcludes] = useState([]);

  const [roundTripIncludes, setRoundTripIncludes] = useState([]);
  const [roundTripExcludes, setRoundTripExcludes] = useState([]);

  const [airportIncludes, setAirportIncludes] = useState([]);
  const [airportExcludes, setAirportExcludes] = useState([]);

  // Terms & Conditions: default 5 preset points (editable)
  const DEFAULT_TERMS = [
    "Your Trip has a KM limit. If your usage exceeds this limit, you will be charged for the excess KM used.",
    "We promote cleaner fuel and thus your cab can be a CNG vehicle. The driver may need to fill CNG once or more during your trip. Please cooperate with the driver.",
    "The Airport Entry/Parking charge, if applicable is not included in the fare and will be charged extra.",
    "Your trip includes one pick up in Pick-up city and one drop to destination city. It does not include within city travel.",
    "If your Trip has Hill climbs, cab AC may be switched off during such climbs.",
  ];
  const [terms, setTerms] = useState([]);


  const fieldConfig = {
    outstationRoundTrip: [
      {
        name: "perKmRate",
        placeholder: "Per Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "extraKmRate",
        placeholder: "Extra Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "driverCharge",
        placeholder: "Driver Charge / Day",
        type: "number",
        isRequired: false,
      },
      {
        name: "waitingCharge",
        placeholder: "Waiting Charge / 30 Minutes",
        type: "number",
        isRequired: false,
      },
      {
        name: "nightCharge",
        placeholder: "Night Charge / Day",
        type: "number",
        isRequired: false,
      },
      {
        name: "tollCharge",
        placeholder: "Toll Charges / KM",
        type: "number",
        isRequired: false,
      },
      {
        name: "minimumKmPerDay",
        placeholder: "Minimum Kilometres / Day",
        type: "number",
        isRequired: true,
      },
      {
        name: "commissionPercent",
        placeholder: "Commission (%)",
        type: "number",
        isRequired: false,
      }

    ],
    outstationOneWay: [
      {
        name: "perKmRate",
        placeholder: "Per Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "extraKmRate",
        placeholder: "Extra Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "driverCharge",
        placeholder: "Driver Charge / Day",
        type: "number",
        isRequired: false,
      },
      {
        name: "waitingCharge",
        placeholder: "Waiting Charge / 30 Minutes",
        type: "number",
        isRequired: false,
      },
      {
        name: "nightCharge",
        placeholder: "Night Charge / Night",
        type: "number",
        isRequired: false,
      },
      {
        name: "tollCharge",
        placeholder: "Toll Charges / KM",
        type: "number",
        isRequired: false,
      },
      {
        name: "minimumKmPerDay",
        placeholder: "Minimum Kilometres / Day",
        type: "number",
        isRequired: true,
      },
      {
        name: "commissionPercent",
        placeholder: "Commission (%)",
        type: "number",
        isRequired: false,
      }

    ],
    airportTransfer: [
      {
        name: "perKmRate",
        placeholder: "Per Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "extraKmRate",
        placeholder: "Extra Kilometre Rate",
        type: "number",
        isRequired: true,
      },
      {
        name: "driverCharge",
        placeholder: "Driver Charge / Day",
        type: "number",
        isRequired: false,
      },
      {
        name: "waitingCharge",
        placeholder: "Waiting Charge / 30 Minutes",
        type: "number",
        isRequired: false,
      },
      {
        name: "nightCharge",
        placeholder: "Night Charge / Night",
        type: "number",
        isRequired: false,
      },
      {
        name: "tollCharge",
        placeholder: "Toll Charges / KM",
        type: "number",
        isRequired: false,
      },
      {
        name: "minimumKmPerDay",
        placeholder: "Minimum Kilometres / Day",
        type: "number",
        isRequired: true,
      },
      {
        name: "commissionPercent",
        placeholder: "Commission (%)",
        type: "number",
        isRequired: false,
      }

    ],
  };

  const validateDateTimeRange = ({ startDate, endDate, startTime, endTime }) => {
    const errors = {
      startDate: "",
      startTime: "",
      endDate: "",
      endTime: "",
      general: "",
    };

    // Check if all fields are filled - these are all required
    if (!startDate) errors.startDate = "Start date is required";
    if (!startTime) errors.startTime = "Start time is required";
    if (!endDate) errors.endDate = "End date is required";
    if (!endTime) errors.endTime = "End time is required";

    // If any field is missing, return early
    if (!startDate || !endDate || !startTime || !endTime) return errors;

    const start = new Date(`${startDate}T${startTime}`);
    const end = new Date(`${endDate}T${endTime}`);

    if (isNaN(start) || isNaN(end)) return errors;

    if (start >= end) {
      if (startDate === endDate) {
        errors.endTime = "End time must be after start time";
      } else {
        errors.endDate = "End date must be after start date";
      }
    }

    return errors;
  };

  const updatePricingWithValidation = (
    setter,
    pricingKey,
    type,
    field,
    value
  ) => {
    setter(prev => {
      const updated = {
        ...prev,
        [type]: {
          ...prev[type],
          [field]: value,
        },
      };

      const fieldErrors = validateDateTimeRange(updated[type]);

      setPricingErrors(prevErr => ({
        ...prevErr,
        [pricingKey]: {
          ...prevErr[pricingKey],
          [type]: fieldErrors,
        },
      }));

      return updated;
    });
  };

  const hasAnyDateTimeError = (err) =>
    !!(err.startDate || err.startTime || err.endDate || err.endTime || err.general);

  const setDecimalFieldError = (errorKey, errorMessage) => {
    setDecimalValidationErrors((prev) => {
      if (!errorKey) return prev;

      if (!errorMessage) {
        if (!prev[errorKey]) return prev;
        const updated = { ...prev };
        delete updated[errorKey];
        return updated;
      }

      if (prev[errorKey] === errorMessage) return prev;
      return {
        ...prev,
        [errorKey]: errorMessage,
      };
    });
  };

  const parseNumericInputWithValidation = (fieldName, value, fallback = 0, errorKey = fieldName) => {
    if (TWO_DECIMAL_FIELDS.has(fieldName)) {
      const result = getProgressiveDecimalInput(value, fallback === 0 ? "" : String(fallback ?? ""));

      if (result.isValid) {
        setDecimalFieldError(errorKey, "");
        return result.value;
      }

      return fallback;
    }

    const parsed = parseNumericInput(fieldName, value, fallback);
    setDecimalFieldError(errorKey, "");
    return parsed;
  };

  const handleDecimalBlur = (fieldName, value, errorKey, applyValue) => {
    if (!TWO_DECIMAL_FIELDS.has(fieldName)) return;

    const invalidDecimalMessage = "Use a valid number with up to 2 decimal places (e.g. 12.34).";

    if (value === "" || value === null || value === undefined) {
      setDecimalFieldError(errorKey, "");
      applyValue?.("");
      return;
    }

    const result = clampToTwoDecimals(value, 0);
    if (!result.isValid) {
      setDecimalFieldError(errorKey, invalidDecimalMessage);
      return;
    }

    const rangeValidation = validateNumericRange(fieldName, result.value);
    if (!rangeValidation.isValid) {
      setDecimalFieldError(errorKey, rangeValidation.message);
      return;
    }

    setDecimalFieldError(errorKey, "");
    applyValue?.(result.value);
  };

  const collectDecimalValidationErrors = () => {
    const nextErrors = {};
    const invalidDecimalMessage = "Use a valid number with up to 2 decimal places (e.g. 12.34).";

    const validateField = (fieldName, value, errorKey) => {
      if (!TWO_DECIMAL_FIELDS.has(fieldName)) return;

      const result = clampToTwoDecimals(value, Number(value) || 0);
      if (!result.isValid) {
        nextErrors[errorKey] = invalidDecimalMessage;
        return;
      }

      const rangeValidation = validateNumericRange(fieldName, result.value);
      if (!rangeValidation.isValid) {
        nextErrors[errorKey] = rangeValidation.message;
      }
    };

    Object.entries(fieldConfig).forEach(([sectionKey, fields]) => {
      const sectionState =
        sectionKey === "outstationOneWay"
          ? outStationOneWay
          : sectionKey === "outstationRoundTrip"
            ? outStationRoundTrip
            : airportTransfer;

      fields.forEach((field) => {
        if (field.type !== "number") return;
        validateField(field.name, sectionState?.[field.name], `${sectionKey}.${field.name}`);
      });
    });

    const sections = [
      ["outstationOneWay", outStationOneWay],
      ["outstationRoundTrip", outStationRoundTrip],
      ["airportTransfer", airportTransfer],
    ];

    sections.forEach(([sectionKey, state]) => {
      validateField("premiumSurcharge", state?.premium?.premiumSurcharge, `${sectionKey}.premium.premiumSurcharge`);
      validateField("discount", state?.discount?.discount, `${sectionKey}.discount.discount`);
      validateField("amount", state?.surcharge?.amount, `${sectionKey}.surcharge.amount`);
    });

    validateField("driverCharge", localRentalGlobal?.driverCharge, "localRentalGlobal.driverCharge");
    validateField("nightCharge", localRentalGlobal?.nightCharge, "localRentalGlobal.nightCharge");
    validateField("premiumSurcharge", localRentalGlobal?.premium?.premiumSurcharge, "localRentalGlobal.premium.premiumSurcharge");
    validateField("discount", localRentalGlobal?.discount?.discount, "localRentalGlobal.discount.discount");
    validateField("amount", localRentalGlobal?.surcharge?.amount, "localRentalGlobal.surcharge.amount");

    localRentals.forEach((rental, index) => {
      validateField("price", rental?.price, `localRental.${index}.price`);
      validateField("extraKmRate", rental?.extraKmRate, `localRental.${index}.extraKmRate`);
      validateField("extraHourRate", rental?.extraHourRate, `localRental.${index}.extraHourRate`);
      validateField("commissionPercent", rental?.commissionPercent, `localRental.${index}.commissionPercent`);
    });

    return nextErrors;
  };

  const handleInputChange = (e, whereToSet, sectionKey = "form") => {
    const { name, value, type } = e.target;
    const errorKey = `${sectionKey}.${name}`;

    if (whereToSet) {
      whereToSet((prevData) => ({
        ...prevData,
        [name]: type === "number"
          ? parseNumericInputWithValidation(
            name,
            value,
            prevData?.[name] ?? data?.[name] ?? 0,
            errorKey
          )
          : value,
      }));
    } else {
      let parsedValue = type === "number"
        ? parseNumericInputWithValidation(name, value, data?.[name] ?? 0, errorKey)
        : value;

      if (name === "luggageCapacity" && type === "number") {
        parsedValue = Math.max(0, Math.min(20, Number(parsedValue) || 0));
      }

      setData((prevData) => ({
        ...prevData,
        [name]: parsedValue,
      }));
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageURL = URL.createObjectURL(file);
      setPreview(imageURL);
      setData((prevData) => ({
        ...prevData,
        carImage: file, // Changed to carImage to match API
      }));
    }
  };

  const handleRemovePreview = () => {
    setPreview(null);
    setData((prevData) => ({
      ...prevData,
      carImage: null,
    }));
  };

  const handleAddLocalRental = () => {
    setLocalRentals((prev) => [
      ...prev,
      createDefaultLocalRental(),
    ]);
  };

  const handleRemoveLocalRental = (index) => {
    setLocalRentals((prev) => prev.filter((_, i) => i !== index));

    setDecimalValidationErrors((prev) => {
      const updated = {};

      Object.entries(prev).forEach(([key, value]) => {
        const match = key.match(/^localRental\.(\d+)\.(.+)$/);

        if (!match) {
          updated[key] = value;
          return;
        }

        const currentIndex = Number(match[1]);
        const fieldName = match[2];

        if (currentIndex === index) {
          return;
        }

        const nextIndex = currentIndex > index ? currentIndex - 1 : currentIndex;
        updated[`localRental.${nextIndex}.${fieldName}`] = value;
      });

      return updated;
    });
  };

  const handleLocalRentalChange = (index, name, value) => {
    setLocalRentals((prev) =>
      prev.map((rental, i) =>
        i === index ? { ...rental, [name]: value } : rental
      )
    );
  };

  const updateLocalRentalGlobalWithValidation = (type, field, value) => {
    setLocalRentalGlobal((prev) => {
      const updated = {
        ...prev,
        [type]: {
          ...prev[type],
          [field]: value,
        },
      };

      const fieldErrors = validateDateTimeRange(updated[type]);
      setLocalRentalPricingErrors((prevErr) => ({
        ...prevErr,
        [type]: fieldErrors,
      }));

      return updated;
    });
  };

  const renderPricingFields = (fields, whereToSet, whereToGetValue, sectionKey) => {
    return fields?.map((field, index) => (
      <GridItem key={field?.name || index}>
        <LabelInput
          placeholder={`Enter ` + field?.placeholder}
          label={field?.placeholder}
          name={field?.name}
          type={field?.type}
          value={whereToGetValue?.[field?.name] ?? ""}
          {...(field?.type === "number" ? getNumericInputProps(field?.name) : {})}
          onChange={(e) => handleInputChange(e, whereToSet, sectionKey)}
          onBlur={(e) => {
            if (field?.type === "number") {
              const errorKey = `${sectionKey}.${field?.name}`;
              handleDecimalBlur(field?.name, e.target.value, errorKey, (nextValue) =>
                whereToSet((prev) => ({
                  ...prev,
                  [field?.name]: nextValue,
                }))
              );
            }
          }}
          isRequired={field?.isRequired ?? true}
        />
        {decimalValidationErrors[`${sectionKey}.${field?.name}`] && (
          <Text color="red.500" fontSize="xs" mt={1}>
            {decimalValidationErrors[`${sectionKey}.${field?.name}`]}
          </Text>
        )}
      </GridItem>
    ));
  };

  const configForPriceFields = [
    {
      heading: "Outstation One Way",
      whereToSet: setOutStationOneWay,
                          key: "outstationOneWay",
      whereToGetValue: outStationOneWay,
    },
    {
      heading: "Outstation Round Trip",
      whereToSet: setOutStationRoundTrip,
      key: "outstationRoundTrip",
      whereToGetValue: outStationRoundTrip,
    },
    {
      heading: "Airport Transfer",
      whereToSet: setAirportTransfer,
      key: "airportTransfer",
      whereToGetValue: airportTransfer,
    },
  ];

  useEffect(() => {
    // 1) Highest priority: carImage (jo Add / Edit form me File ya URL ho sakta hai)
    let imageSource = null;

    if (data?.carImage) {
      imageSource = data.carImage;
    } else if (Array.isArray(data?.images) && data.images.length > 0) {
      // 2) Fallback: backend se aayi images array
      imageSource = data.images[0];
    }

    if (!imageSource) {
      setPreview(null);
      return;
    }

    // If string URL (from backend)
    if (typeof imageSource === "string") {
      setPreview(imageSource);
      return;
    }

    // If File object (from file input)
    if (imageSource instanceof File) {
      const objectUrl = URL.createObjectURL(imageSource);
      setPreview(objectUrl);

      return () => {
        URL.revokeObjectURL(objectUrl);
      };
    }
  }, [data?.carImage, data?.images]);

  const formatDate = (date) => {
    if (!date) return "";
    return date.split("T")[0];
  };

  const formatTime = (time) => {
    if (!time) return "";
    return time.slice(0, 5);
  };

  const getCurrentDate = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const getCurrentTime = () => {
    const now = new Date();
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  useEffect(() => {
    if (data?.pricing?.localRental) {
      const parsedLocalRentals = data.pricing.localRental.map((rental) => ({
        durationHours: Number(rental?.durationHours || 0),
        includedKm: Number(rental?.includedKm || 0),
        price: Number(rental?.price || 0),
        extraKmRate: Number(rental?.extraKmRate || 0),
        extraHourRate: Number(rental?.extraHourRate || 0),
        commissionPercent: Number(rental?.commissionPercent || 0),
      }));

      setLocalRentals(
        parsedLocalRentals.length > 0 ? parsedLocalRentals : [createDefaultLocalRental()]
      );

      const firstRental = data.pricing.localRental[0] || {};
      const savedIncludes = Array.isArray(firstRental?.includes) ? firstRental.includes : [];
      setLocalRentalIncludes(savedIncludes);
      setLocalRentalExcludes(
        pricingOptions.filter((item) => !savedIncludes.includes(item))
      );

      setLocalRentalGlobal((prev) => ({
        ...prev,
        driverCharge: Number(firstRental?.driverCharge || 0),
        nightCharge: Number(firstRental?.nightCharge || firstRental?.nightAllowanceCharge || 0),
        premium: {
          ...prev.premium,
          ...(firstRental?.premium || {}),
        },
        discount: {
          ...prev.discount,
          ...(firstRental?.discount || {}),
          startDate: formatDate(firstRental?.discount?.startDate),
          endDate: formatDate(firstRental?.discount?.endDate),
          startTime: formatTime(firstRental?.discount?.startTime),
          endTime: formatTime(firstRental?.discount?.endTime),
        },
        surcharge: {
          ...prev.surcharge,
          ...(firstRental?.surcharge || {}),
          startDate: formatDate(firstRental?.surcharge?.startDate),
          endDate: formatDate(firstRental?.surcharge?.endDate),
          startTime: formatTime(firstRental?.surcharge?.startTime),
          endTime: formatTime(firstRental?.surcharge?.endTime),
        },
      }));

      setLocalRentalPricingErrors({
        discount: createEmptyDateTimeErrors(),
        surcharge: createEmptyDateTimeErrors(),
      });
    }

    if (data?.pricing?.outstationOneWay) {
      setOutStationOneWay(prev => ({
        ...prev,
        ...data.pricing.outstationOneWay,
        commissionPercent: Number(data.pricing.outstationOneWay?.commissionPercent || 0),
        premium: {
          ...prev.premium,
          ...data.pricing.outstationOneWay?.premium,
        },
        discount: {
          ...prev.discount,
          ...data.pricing.outstationOneWay?.discount,
          startDate: formatDate(data.pricing.outstationOneWay?.discount?.startDate),
          endDate: formatDate(data.pricing.outstationOneWay?.discount?.endDate),
          startTime: formatTime(data.pricing.outstationOneWay?.discount?.startTime),
          endTime: formatTime(data.pricing.outstationOneWay?.discount?.endTime),
        },
        surcharge: {
          ...prev.surcharge,
          ...data.pricing.outstationOneWay?.surcharge,
          startDate: formatDate(data.pricing.outstationOneWay?.surcharge?.startDate),
          endDate: formatDate(data.pricing.outstationOneWay?.surcharge?.endDate),
          startTime: formatTime(data.pricing.outstationOneWay?.surcharge?.startTime),
          endTime: formatTime(data.pricing.outstationOneWay?.surcharge?.endTime),
        },
      }));

      // restore saved includes/excludes for Outstation One Way
      const savedIncludes = data.pricing.outstationOneWay.includes || [];
      setOutstationIncludes(savedIncludes);
      setOutstationExcludes(
        pricingOptions.filter(item => !savedIncludes.includes(item))
      );

      // restore saved includes/excludes for Outstation One Way with Stops
      const savedIncludesWithStops = data.pricing.outstationOneWay.includesWithStops || [];
      setOutstationWithStopsIncludes(savedIncludesWithStops);
      setOutstationWithStopsExcludes(
        pricingOptions.filter(item => !savedIncludesWithStops.includes(item))
      );

    }

    if (data?.pricing?.outstationRoundTrip) {
      setOutStationRoundTrip(prev => ({
        ...prev,
        ...data.pricing.outstationRoundTrip,
        commissionPercent: Number(data.pricing.outstationRoundTrip?.commissionPercent || 0),
        premium: {
          ...prev.premium,
          ...data.pricing.outstationRoundTrip?.premium,
        },
        discount: {
          ...prev.discount,
          ...data.pricing.outstationRoundTrip?.discount,
          startDate: formatDate(data.pricing.outstationRoundTrip?.discount?.startDate),
          endDate: formatDate(data.pricing.outstationRoundTrip?.discount?.endDate),
          startTime: formatTime(data.pricing.outstationRoundTrip?.discount?.startTime),
          endTime: formatTime(data.pricing.outstationRoundTrip?.discount?.endTime),
        },
        surcharge: {
          ...prev.surcharge,
          ...data.pricing.outstationRoundTrip?.surcharge,
          startDate: formatDate(data.pricing.outstationRoundTrip?.surcharge?.startDate),
          endDate: formatDate(data.pricing.outstationRoundTrip?.surcharge?.endDate),
          startTime: formatTime(data.pricing.outstationRoundTrip?.surcharge?.startTime),
          endTime: formatTime(data.pricing.outstationRoundTrip?.surcharge?.endTime),
        },
      }));
      // restore saved includes/excludes for Round Trip
      const savedIncludes = data.pricing.outstationRoundTrip.includes || [];
      setRoundTripIncludes(savedIncludes);
      setRoundTripExcludes(
        pricingOptions.filter(item => !savedIncludes.includes(item))
      );

    }

    if (data?.pricing?.airportTransfer) {
      setAirportTransfer(prev => ({
        ...prev,
        ...data.pricing.airportTransfer,
        commissionPercent: Number(data.pricing.airportTransfer?.commissionPercent || 0),
        premium: {
          ...prev.premium,
          ...data.pricing.airportTransfer?.premium,
        },
        discount: {
          ...prev.discount,
          ...data.pricing.airportTransfer?.discount,
          startDate: formatDate(data.pricing.airportTransfer?.discount?.startDate),
          endDate: formatDate(data.pricing.airportTransfer?.discount?.endDate),
          startTime: formatTime(data.pricing.airportTransfer?.discount?.startTime),
          endTime: formatTime(data.pricing.airportTransfer?.discount?.endTime),
        },
        surcharge: {
          ...prev.surcharge,
          ...data.pricing.airportTransfer?.surcharge,
          startDate: formatDate(data.pricing.airportTransfer?.surcharge?.startDate),
          endDate: formatDate(data.pricing.airportTransfer?.surcharge?.endDate),
          startTime: formatTime(data.pricing.airportTransfer?.surcharge?.startTime),
          endTime: formatTime(data.pricing.airportTransfer?.surcharge?.endTime),
        },
      }));
      // restore saved includes/excludes for Airport Transfer
      const savedIncludes = data.pricing.airportTransfer.includes || [];
      setAirportIncludes(savedIncludes);
      setAirportExcludes(
        pricingOptions.filter(item => !savedIncludes.includes(item))
      );

    }
  }, [data]);

  const hasHydratedTerms = useRef(false);

  // Initialize terms; in edit mode wait for DB data, in add use defaults immediately
  useEffect(() => {
    hasHydratedTerms.current = false;
  }, [isEdit]);

  useEffect(() => {
    if (hasHydratedTerms.current) return;

    // wait until backend data arrives
    if (isEdit && data?.termsAndConditions === undefined) return;

    if (isEdit) {
      setTerms(
        (data.termsAndConditions ?? []).map(t => ({
          id: crypto.randomUUID(),
          text: t,
        }))
      );
    } else {
      setTerms(
        DEFAULT_TERMS.map(t => ({
          id: crypto.randomUUID(),
          text: t,
        }))
      );
    }

    hasHydratedTerms.current = true;
  }, [isEdit, data?.termsAndConditions]);


  // Keep cleaned non-empty terms array in main data for submit
  useEffect(() => {
    if (!hasHydratedTerms.current) return;
    const cleaned = terms
      .map(t => t.text.trim())
      .filter(Boolean);

    setData(prev => ({
      ...prev,
      termsAndConditions: cleaned,
    }));
  }, [terms, setData]);

  // Re-validate only after first submit attempt to avoid showing errors on fresh form load.
  useEffect(() => {
    if (!hasHydratedTerms.current || !hasAttemptedSubmit) return;
    validateForm();
  }, [
    hasAttemptedSubmit,
    outStationOneWay,
    outStationRoundTrip,
    airportTransfer,
    data,
    terms,
    localRentals,
    localRentalGlobal,
    localRentalIncludes,
  ]);

  const validatePricing = (pricing, pricingKey, label) => {
  let errorMessage = "";

  // -------- PREMIUM --------
  if (pricing.premium.isPremium) {
    if (!pricing.premium.premiumSurcharge || pricing.premium.premiumSurcharge <= 0) {
      errorMessage = `${label} - Premium amount must be greater than 0`;
    }

    const validPremiumAddons = Array.isArray(pricing.premium.addons)
      ? pricing.premium.addons.map((addon) => String(addon).trim()).filter(Boolean)
      : [];

    if (validPremiumAddons.length === 0) {
      errorMessage = `${label} - At least one premium addon required`;
    }
  }

  // -------- DISCOUNT --------
  if (pricing.discount.isDiscountApply) {
    if (!pricing.discount.discount || pricing.discount.discount <= 0 || pricing.discount.discount > 100) {
      errorMessage = `${label} - Discount % must be between 1 to 100`;
    }

    // Validate all date/time fields are filled for discount
    if (!pricing.discount.startDate || !pricing.discount.startTime || !pricing.discount.endDate || !pricing.discount.endTime) {
      errorMessage = `${label} - All discount date/time fields (start date, start time, end date, end time) are required`;
    }

    // Validate date/time range if all fields are filled
    if (pricing.discount.startDate && pricing.discount.startTime && pricing.discount.endDate && pricing.discount.endTime) {
      const discountErrors = validateDateTimeRange(pricing.discount);
      if (hasAnyDateTimeError(discountErrors)) {
        errorMessage = `${label} - Fix Discount date/time range`;
      }
    }
  }

  // -------- SURCHARGE --------
  if (pricing.surcharge.isSurchargeApply) {
    if (!pricing.surcharge.amount || pricing.surcharge.amount <= 0) {
      errorMessage = `${label} - Surcharge amount must be greater than 0`;
    }

    // Validate all date/time fields are filled for surcharge
    if (!pricing.surcharge.startDate || !pricing.surcharge.startTime || !pricing.surcharge.endDate || !pricing.surcharge.endTime) {
      errorMessage = `${label} - All surcharge date/time fields (start date, start time, end date, end time) are required`;
    }

    // Validate date/time range if all fields are filled
    if (pricing.surcharge.startDate && pricing.surcharge.startTime && pricing.surcharge.endDate && pricing.surcharge.endTime) {
      const surchargeErrors = validateDateTimeRange(pricing.surcharge);
      if (hasAnyDateTimeError(surchargeErrors)) {
        errorMessage = `${label} - Fix Surcharge date/time range`;
      }
    }
  }

  return errorMessage;
};

  const validateLocalRentalGlobalPricing = () => {
    const pricingToValidate = {
      premium: localRentalGlobal.premium,
      discount: localRentalGlobal.discount,
      surcharge: localRentalGlobal.surcharge,
    };
    return validatePricing(pricingToValidate, "localRental", "Local Rental");
  };

  const validateForm = () => {
    let newErrors = validateBasicVehicleFields(data);

    // -------- PRICING REQUIRED --------
    if (!outStationOneWay.perKmRate || !outStationOneWay.minimumKmPerDay || !outStationOneWay.extraKmRate) {
      newErrors.outstationOneWay = "All Outstation One Way pricing is required";
    }

    if (!outStationRoundTrip.perKmRate || !outStationRoundTrip.minimumKmPerDay) {
      newErrors.outstationRoundTrip = "Outstation Round Trip pricing is required";
    }

    if (!airportTransfer.perKmRate || !airportTransfer.minimumKmPerDay) {
      newErrors.airportTransfer = "Airport Transfer pricing is required";
    }

    // pricing errors
    const outstationOneWayError = validatePricing(outStationOneWay, "outstationOneWay", "Outstation One Way");
    if (outstationOneWayError) {
      newErrors.outstationOneWay = outstationOneWayError;
    }

    const outstationRoundTripError = validatePricing(outStationRoundTrip, "outstationRoundTrip", "Outstation Round Trip");
    if (outstationRoundTripError) {
      newErrors.outstationRoundTrip = outstationRoundTripError;
    }

    const airportTransferError = validatePricing(airportTransfer, "airportTransfer", "Airport Transfer");
    if (airportTransferError) {
      newErrors.airportTransfer = airportTransferError;
    }

    const localRentalGlobalError = validateLocalRentalGlobalPricing();
    if (localRentalGlobalError) {
      newErrors.localRental = localRentalGlobalError;
    }

    if (
      outStationOneWay.commissionPercent < 0 ||
      outStationOneWay.commissionPercent > 100
    ) {
      newErrors.outstationOneWay =
        newErrors.outstationOneWay || "Outstation One Way - Commission % must be between 0 and 100";
    }

    if (
      outStationRoundTrip.commissionPercent < 0 ||
      outStationRoundTrip.commissionPercent > 100
    ) {
      newErrors.outstationRoundTrip =
        newErrors.outstationRoundTrip || "Outstation Round Trip - Commission % must be between 0 and 100";
    }

    if (
      airportTransfer.commissionPercent < 0 ||
      airportTransfer.commissionPercent > 100
    ) {
      newErrors.airportTransfer =
        newErrors.airportTransfer || "Airport Transfer - Commission % must be between 0 and 100";
    }

    const hasInvalidLocalRentalCommission = localRentals.some(
      (rental) => rental?.commissionPercent < 0 || rental?.commissionPercent > 100
    );

    if (hasInvalidLocalRentalCommission) {
      newErrors.localRental =
        newErrors.localRental || "Local Rental - Commission % must be between 0 and 100";
    }

    if (!localRentals?.length) {
      newErrors.localRental = "At least one local rental is required";
    }

    // -------- TERMS --------
    const nonEmptyTerms = terms.filter(t => t.text.trim());
    if (nonEmptyTerms.length === 0) {
      newErrors.terms = "At least one term must be filled";
    }

    setErrors(newErrors);
    return newErrors;
  };

  const scrollToFirstError = (errorObj) => {
    const firstKey = Object.keys(errorObj)[0];
    if (firstKey.includes("outstation") || firstKey.includes("airport") || firstKey.includes("localRental")) {
      setTimeout(() => {
        document.querySelector("h2")?.scrollIntoView({
          behavior: "smooth",
        });
      }, 100);
      return;
    }
    const el = document.querySelector(`[name="${firstKey}"]`);
    el?.scrollIntoView({ behavior: "smooth", block: "center" });
    el?.focus();
  };

  const hasBasicInput =
    Boolean(data?.segmentName?.trim()) ||
    Boolean(data?.description?.trim()) ||
    Boolean(data?.vehicleTypes?.trim()) ||
    Number(data?.passengerCapacity || 0) > 0 ||
    Number(data?.luggageCapacity || 0) > 0 ||
    Boolean(data?.status) ||
    (Array.isArray(data?.fuelTypes) && data.fuelTypes.length > 0) ||
    Boolean(
      typeof data?.amenities === "string"
        ? data.amenities.trim()
        : data?.amenities?.length
    ) ||
    Boolean(data?.carImage);

  const isSaveEnabled = isEdit || hasBasicInput;

  return (
    <Box >
      <form
        ref={formRef}
        onSubmit={async (e) => {
          e.preventDefault();
          setHasAttemptedSubmit(true);

          const computedDecimalErrors = collectDecimalValidationErrors();
          setDecimalValidationErrors(computedDecimalErrors);

          if (Object.keys(computedDecimalErrors).length > 0) {
            toast({
              title: "Validation Error",
              description: "Please fix invalid numeric values. Use up to 2 decimal places.",
              status: "error",
              duration: 4000,
              isClosable: true,
            });
            return;
          }

          const validationErrors = validateForm();

          if (Object.keys(validationErrors).length > 0) {
            toast({
              title: "Validation Error",
              description: Object.values(validationErrors)[0],
              status: "error",
              duration: 4000,
              isClosable: true,
            });

            scrollToFirstError(validationErrors);
            return;
          }

          setIsSaving(true);

          const localRentalComputedExcludes = pricingOptions.filter(
            (opt) => !localRentalIncludes.includes(opt)
          );

          const localRentalsWithGlobalPricing = localRentals.map((rental) => ({
            ...rental,
            driverCharge: Number(localRentalGlobal.driverCharge) || 0,
            nightCharge: Number(localRentalGlobal.nightCharge) || 0,
            includes: localRentalIncludes,
            excludes: localRentalComputedExcludes,
            premium: {
              ...localRentalGlobal.premium,
              addons: Array.isArray(localRentalGlobal.premium?.addons)
                ? localRentalGlobal.premium.addons
                : [],
            },
            discount: {
              ...localRentalGlobal.discount,
            },
            surcharge: {
              ...localRentalGlobal.surcharge,
            },
          }));

          await handleSubmit(
            e,
            localRentalsWithGlobalPricing,
            {
              ...outStationOneWay,
              includes: outstationIncludes,
              excludes: outstationExcludes,
              includesWithStops: outstationWithStopsIncludes,
              excludesWithStops: outstationWithStopsExcludes,
            },
            {
              ...outStationRoundTrip,
              includes: roundTripIncludes,
              excludes: roundTripExcludes,
            },
            {
              ...airportTransfer,
              includes: airportIncludes,
              excludes: airportExcludes,
            }
          );

          setIsSaving(false);
        }}

      >

        <Box px={6} py={8} borderRadius={"lg"} bg={"white"} my={"2rem"}>

          <Heading fontSize="lg" mb={1} color={"#1A202C"}>
            Basic Information
          </Heading>
          <Flex justifyContent={"space-between"} fontSize={"0.875rem"} letterSpacing={0} mb={6}>
            <Text color={"#90A3BF"}>Please Basic Info</Text>
            <Text color={"#90A3BF"}>Step 1 of 4</Text>
          </Flex>
          <Grid
            templateColumns={{
              base: "1fr",
              md: "repeat(2, 1fr)",
              lg: "repeat(4,1fr)",
            }}
            gap={4}
            mb={6}
          >
            <GridItem pos={"relative"}>
              <LabelInput
                placeholder="Segment Name (Hatchback,Sedan..)"
                label="Segment Name"
                name="segmentName"
                maxLength={20}
                value={data?.segmentName || ""}
                onChange={(e) => {
                  // Allow only alphabets and spaces
                  const filteredValue = e.target.value.replace(/[^a-zA-Z\s]/g, "");
                  setData((prevData) => ({
                    ...prevData,
                    segmentName: filteredValue,
                  }));
                }}
              // isRequired={true}
              />
              {errors.segmentName && (
                <Text color="red.500" fontSize="xs">
                  {errors.segmentName}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <LabelInput
                placeholder="Enter Description"
                label="Description"
                name="description"
                value={data?.description || ""}
                onChange={handleInputChange}
              //isRequired={true}
              />
            </GridItem>
            {/* <GridItem colSpan={2}></GridItem> */}
            <GridItem>
              <LabelInput
                placeholder="Enter Vehicle Type"
                label="Vehicle Type"
                name="vehicleTypes"
                maxLength={20}
                value={data?.vehicleTypes || ""}
                onChange={handleInputChange}
              // isRequired={true}
              />
              {errors.vehicleTypes && (
                <Text color="red.500" fontSize="xs">
                  {errors.vehicleTypes}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <LabelInput
                placeholder="Enter Luggage Capacity"
                label="Luggage Capacity"
                name="luggageCapacity"
                value={data?.luggageCapacity || ""}
                onChange={handleInputChange}
                // isRequired={true}
                type={"number"}
                min={0}
                max={20}
                step={1}
              />
              {errors.luggageCapacity && (
                <Text color="red.500" fontSize="xs">
                  {errors.luggageCapacity}
                </Text>
              )}
            </GridItem>
            <GridItem>
              <LabelInput
                placeholder="Enter Passenger Capacity"
                label="Passenger Capacity"
                name="passengerCapacity"
                value={data?.passengerCapacity || ""}
                onChange={handleInputChange}
                // isRequired={true}
                type={"number"}
                min={0}
                max={15}
                step={1}
              />
              {errors.passengerCapacity && (
                <Text color="red.500" fontSize="xs">
                  {errors.passengerCapacity}
                </Text>
              )}
            </GridItem>
            <GridItem >
              <LabelInput
                placeholder="Enter Amenities"
                label="Amenities"
                name="amenities"
                value={data?.amenities || ""}
                onChange={handleInputChange}
              // isRequired={true}
              />
            </GridItem>
            <GridItem>
              <MultiSelect
                value={data?.fuelTypes || []}
                name={"fuelTypes"}
                listvalue={fuelTypes}
                onChangeHandler={handleInputChange}
              />
            </GridItem>
          </Grid>
        </Box>

        {/* Pricing and Rates Section */}
        <Flex justifyContent={"space-between"} mb={6} mt={"2rem"}>
          <Heading fontSize="md">
            Pricing And Rates
          </Heading>
          <Text color={"#90A3BF"} fontSize={"0.875rem"} letterSpacing={0}>Step 2 of 4</Text>
        </Flex>

        {Object.keys(decimalValidationErrors).length > 0 && (
          <Text color="red.500" fontSize="sm" mb={4}>
            Please fix invalid numeric values. Use up to 2 decimal places.
          </Text>
        )}

        {configForPriceFields?.map((field) => (
          <Box key={field.heading} px={4} py={4} borderRadius={"lg"} bg={"white"} mb={4}>
            <Accordion
              allowToggle
              w="100%"
            >
              <AccordionItem border="none" >
                {({ isExpanded }) => (
                  <Box
                    border={errors[field.key] ? "2px solid red" : "1px solid #CBD5E0"}
                    borderRadius="md"
                    w={isExpanded ? "100%" : "100%"}
                    transition="height width 0.3s ease">
                    <AccordionButton w={"100%"}>
                      <Box
                        flex="1"
                        textAlign="left"
                        fontSize="1rem !important"
                        color="#1A202C !important"
                      >
                        {errors[field.key] && (
                          <Text color="red.500" fontSize="sm" mb={1}>
                            {errors[field.key]}
                          </Text>
                        )}
                        {field.heading}
                      </Box>
                      <AccordionIcon />
                    </AccordionButton>
                    <AccordionPanel>
                      <Grid
                        templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }}
                        templateRows={"1fr"}
                        gap={4}
                        mb={6}
                      >
                        {renderPricingFields(
                          fieldConfig[field.key],
                          field.whereToSet,
                          field.whereToGetValue,
                          field.key
                        )}
                      </Grid>

                      {field.heading === "Outstation One Way" && (
                        <PricingExtrasSection
                          sectionKey="outstationOneWay"
                          pricingState={outStationOneWay}
                          setPricingState={setOutStationOneWay}
                          decimalValidationErrors={decimalValidationErrors}
                          setDecimalValidationErrors={setDecimalValidationErrors}
                          parseNumericInputWithValidation={parseNumericInputWithValidation}
                          pricingErrors={pricingErrors}
                          setPricingErrors={setPricingErrors}
                          hasAnyDateTimeError={hasAnyDateTimeError}
                          updatePricingWithValidation={updatePricingWithValidation}
                          getCurrentDate={getCurrentDate}
                          getCurrentTime={getCurrentTime}
                          handleDecimalBlur={handleDecimalBlur}
                        />
                      )}

                      {/* PREMIUM/DISCOUNT/SURCHARGE for Airport Transfer */}
                      {field.heading === "Airport Transfer" && (
                        <PricingExtrasSection
                          sectionKey="airportTransfer"
                          pricingState={airportTransfer}
                          setPricingState={setAirportTransfer}
                          decimalValidationErrors={decimalValidationErrors}
                          setDecimalValidationErrors={setDecimalValidationErrors}
                          parseNumericInputWithValidation={parseNumericInputWithValidation}
                          pricingErrors={pricingErrors}
                          setPricingErrors={setPricingErrors}
                          hasAnyDateTimeError={hasAnyDateTimeError}
                          updatePricingWithValidation={updatePricingWithValidation}
                          getCurrentDate={getCurrentDate}
                          getCurrentTime={getCurrentTime}
                          handleDecimalBlur={handleDecimalBlur}
                        />
                      )}

                      {field.heading === "Outstation Round Trip" && (
                        <PricingExtrasSection
                          sectionKey="outstationRoundTrip"
                          pricingState={outStationRoundTrip}
                          setPricingState={setOutStationRoundTrip}
                          decimalValidationErrors={decimalValidationErrors}
                          setDecimalValidationErrors={setDecimalValidationErrors}
                          parseNumericInputWithValidation={parseNumericInputWithValidation}
                          pricingErrors={pricingErrors}
                          setPricingErrors={setPricingErrors}
                          hasAnyDateTimeError={hasAnyDateTimeError}
                          updatePricingWithValidation={updatePricingWithValidation}
                          getCurrentDate={getCurrentDate}
                          getCurrentTime={getCurrentTime}
                          handleDecimalBlur={handleDecimalBlur}
                        />
                      )}


                      {/* Include/Exclude for Outstation One Way */}
                      {field.heading === "Outstation One Way" && (
                        <Box mt={4}>
                          <Heading fontSize="sm" mb={2}>
                            Includes/Excludes
                          </Heading>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                            {pricingOptions.map((item) => {
                              const isChecked = outstationIncludes.includes(item);

                              return (
                                <Flex key={item} align="center" gap={2}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let updatedIncludes;

                                      if (e.target.checked) {
                                        updatedIncludes = [...outstationIncludes, item];
                                      } else {
                                        updatedIncludes = outstationIncludes.filter(i => i !== item);
                                      }

                                      setOutstationIncludes(updatedIncludes);
                                      setOutstationExcludes(
                                        pricingOptions.filter(opt => !updatedIncludes.includes(opt))
                                      );
                                    }}
                                  />
                                  <Text fontSize="sm">{item}</Text>
                                </Flex>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      {/* Include/Exclude for Outstation One Way with Stops */}
                      {field.heading === "Outstation One Way" && (
                        <Box mt={6}>
                          <Heading fontSize="sm" mb={2}>
                            Includes/Excludes for Oneway with Stops
                          </Heading>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                            {pricingOptions.map((item) => {
                              const isChecked = outstationWithStopsIncludes.includes(item);

                              return (
                                <Flex key={`${item}-with-stops`} align="center" gap={2}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let updatedIncludes;

                                      if (e.target.checked) {
                                        updatedIncludes = [...outstationWithStopsIncludes, item];
                                      } else {
                                        updatedIncludes = outstationWithStopsIncludes.filter(i => i !== item);
                                      }

                                      setOutstationWithStopsIncludes(updatedIncludes);
                                      setOutstationWithStopsExcludes(
                                        pricingOptions.filter(opt => !updatedIncludes.includes(opt))
                                      );
                                    }}
                                  />
                                  <Text fontSize="sm">{item}</Text>
                                </Flex>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      {/* Include/Exclude for Outstation Round Trip */}
                      {field.heading === "Outstation Round Trip" && (
                        <Box mt={4}>
                          <Heading fontSize="sm" mb={2}>
                            Includes/Excludes
                          </Heading>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                            {pricingOptions.map((item) => {
                              const isChecked = roundTripIncludes.includes(item);

                              return (
                                <Flex key={item} align="center" gap={2}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let updatedIncludes;

                                      if (e.target.checked) {
                                        updatedIncludes = [...roundTripIncludes, item];
                                      } else {
                                        updatedIncludes = roundTripIncludes.filter(i => i !== item);
                                      }

                                      setRoundTripIncludes(updatedIncludes);
                                      setRoundTripExcludes(
                                        pricingOptions.filter(opt => !updatedIncludes.includes(opt))
                                      );
                                    }}
                                  />
                                  <Text fontSize="sm">{item}</Text>
                                </Flex>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                      {/* Include/Exclude for Airport Transfer */}
                      {field.heading === "Airport Transfer" && (
                        <Box mt={4}>
                          <Heading fontSize="sm" mb={2}>
                            Includes/Excludes
                          </Heading>

                          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                            {pricingOptions.map((item) => {
                              const isChecked = airportIncludes.includes(item);

                              return (
                                <Flex key={item} align="center" gap={2}>
                                  <input
                                    type="checkbox"
                                    checked={isChecked}
                                    onChange={(e) => {
                                      let updatedIncludes;

                                      if (e.target.checked) {
                                        updatedIncludes = [...airportIncludes, item];
                                      } else {
                                        updatedIncludes = airportIncludes.filter(i => i !== item);
                                      }

                                      setAirportIncludes(updatedIncludes);
                                      setAirportExcludes(
                                        pricingOptions.filter(opt => !updatedIncludes.includes(opt))
                                      );
                                    }}
                                  />
                                  <Text fontSize="sm">{item}</Text>
                                </Flex>
                              );
                            })}
                          </Grid>
                        </Box>
                      )}

                    </AccordionPanel>
                  </Box>
                )}
              </AccordionItem>
            </Accordion>
          </Box>
        ))}

        <Box px={6} py={8} borderRadius={"lg"} bg={"white"} mb={4}>
          <Accordion allowToggle w="100%">
            <AccordionItem border="none">
              {({ isExpanded }) => (
                <Box
                  border="1px solid #CBD5E0"
                  borderRadius="md"
                  w={isExpanded ? "100%" : "100%"}
                  transition="height width 0.3s ease"
                >
                  <AccordionButton w={"100%"}>
                    <Box
                      flex="1"
                      textAlign="left"
                      fontSize="1rem !important"
                      color="#1A202C !important"
                    >
                      {errors.localRental && (
                        <Text color="red.500" fontSize="sm" mb={1}>
                          {errors.localRental}
                        </Text>
                      )}
                      Local Rental
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>

                  <AccordionPanel>
                    <Heading fontSize="sm" mb={4}>
                      Global Local Rental Pricing (applies to all local rental packages)
                    </Heading>

                    <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mb={4}>
                      <LabelInput
                        placeholder="Enter Driver Charge / Day"
                        label="Driver Charge / Day"
                        name="localRentalDriverCharge"
                        type={"number"}
                        min={0}
                        step={0.01}
                        value={localRentalGlobal.driverCharge ?? ""}
                        onChange={(e) =>
                          setLocalRentalGlobal((prev) => ({
                            ...prev,
                            driverCharge: parseNumericInputWithValidation(
                              "driverCharge",
                              e.target.value,
                              prev.driverCharge ?? 0,
                              "localRentalGlobal.driverCharge"
                            ),
                          }))
                        }
                        onBlur={(e) =>
                          handleDecimalBlur(
                            "driverCharge",
                            e.target.value,
                            "localRentalGlobal.driverCharge",
                            (nextValue) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                driverCharge: nextValue,
                              }))
                          )
                        }
                      />
                      {decimalValidationErrors["localRentalGlobal.driverCharge"] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {decimalValidationErrors["localRentalGlobal.driverCharge"]}
                        </Text>
                      )}
                      <LabelInput
                        placeholder="Enter Night Charge"
                        label="Night Charge"
                        name="localRentalNightCharge"
                        type={"number"}
                        min={0}
                        step={0.01}
                        value={localRentalGlobal.nightCharge ?? ""}
                        onChange={(e) =>
                          setLocalRentalGlobal((prev) => ({
                            ...prev,
                            nightCharge: parseNumericInputWithValidation(
                              "nightCharge",
                              e.target.value,
                              prev.nightCharge ?? 0,
                              "localRentalGlobal.nightCharge"
                            ),
                          }))
                        }
                        onBlur={(e) =>
                          handleDecimalBlur(
                            "nightCharge",
                            e.target.value,
                            "localRentalGlobal.nightCharge",
                            (nextValue) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                nightCharge: nextValue,
                              }))
                          )
                        }
                      />
                      {decimalValidationErrors["localRentalGlobal.nightCharge"] && (
                        <Text color="red.500" fontSize="xs" mt={1}>
                          {decimalValidationErrors["localRentalGlobal.nightCharge"]}
                        </Text>
                      )}
                    </Grid>

                    <Box mb={4}>
                      <Heading fontSize="sm" mb={2}>
                        Includes/Excludes
                      </Heading>

                      <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={2}>
                        {pricingOptions.map((item) => {
                          const isChecked = localRentalIncludes.includes(item);

                          return (
                            <Flex key={`${item}-local-rental-global`} align="center" gap={2}>
                              <input
                                type="checkbox"
                                checked={isChecked}
                                onChange={(e) => {
                                  let updatedIncludes;

                                  if (e.target.checked) {
                                    updatedIncludes = [...localRentalIncludes, item];
                                  } else {
                                    updatedIncludes = localRentalIncludes.filter((i) => i !== item);
                                  }

                                  setLocalRentalIncludes(updatedIncludes);
                                  setLocalRentalExcludes(
                                    pricingOptions.filter((opt) => !updatedIncludes.includes(opt))
                                  );
                                }}
                              />
                              <Text fontSize="sm">{item}</Text>
                            </Flex>
                          );
                        })}
                      </Grid>
                    </Box>

                    <Box mt={6} mb={8}>
                      <Flex align="center" gap={2} mb={2}>
                        <Switch
                          isChecked={Boolean(localRentalGlobal?.premium?.isPremium)}
                          onChange={(e) =>
                            setLocalRentalGlobal((prev) => ({
                              ...prev,
                              premium: {
                                ...prev.premium,
                                isPremium: e.target.checked,
                              },
                            }))
                          }
                        />
                        <Text>Premium Service</Text>
                      </Flex>

                      {localRentalGlobal?.premium?.isPremium && (
                        <Grid
                          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                          gap={4}
                          mt={3}
                        >
                          <LabelInput
                            label="Premium Amount"
                            type="number"
                            min={0}
                            step={0.01}
                            value={localRentalGlobal?.premium?.premiumSurcharge ?? ""}
                            onChange={(e) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                premium: {
                                  ...prev.premium,
                                  premiumSurcharge: parseNumericInputWithValidation(
                                    "premiumSurcharge",
                                    e.target.value,
                                    prev.premium?.premiumSurcharge ?? 0,
                                    "localRentalGlobal.premium.premiumSurcharge"
                                  ),
                                },
                              }))
                            }
                            onBlur={(e) =>
                              handleDecimalBlur(
                                "premiumSurcharge",
                                e.target.value,
                                "localRentalGlobal.premium.premiumSurcharge",
                                (nextValue) =>
                                  setLocalRentalGlobal((prev) => ({
                                    ...prev,
                                    premium: {
                                      ...prev.premium,
                                      premiumSurcharge: nextValue,
                                    },
                                  }))
                              )
                            }
                          />
                          {decimalValidationErrors["localRentalGlobal.premium.premiumSurcharge"] && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                              {decimalValidationErrors["localRentalGlobal.premium.premiumSurcharge"]}
                            </Text>
                          )}

                          <LabelInput
                            label="Premium Addons"
                            value={(localRentalGlobal?.premium?.addons || []).join(",")}
                            onChange={(e) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                premium: {
                                  ...prev.premium,
                                  addons: e.target.value
                                    .split(",")
                                    .map((addon) => addon.trim())
                                    .filter(Boolean),
                                },
                              }))
                            }
                          />
                        </Grid>
                      )}

                      <Flex align="center" gap={2} mt={4}>
                        <Switch
                          isChecked={Boolean(localRentalGlobal?.discount?.isDiscountApply)}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            if (!checked) {
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                discount: {
                                  ...prev.discount,
                                  isDiscountApply: false,
                                  discount: 0,
                                  startDate: "",
                                  startTime: "",
                                  endDate: "",
                                  endTime: "",
                                },
                              }));

                              setLocalRentalPricingErrors((prev) => ({
                                ...prev,
                                discount: createEmptyDateTimeErrors(),
                              }));
                            } else {
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                discount: {
                                  ...prev.discount,
                                  isDiscountApply: true,
                                  startDate: getCurrentDate(),
                                  startTime: getCurrentTime(),
                                  endDate: "",
                                  endTime: "",
                                },
                              }));
                            }
                          }}
                        />
                        <Text>Apply Discount</Text>
                      </Flex>

                      {localRentalGlobal?.discount?.isDiscountApply && (
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mt={3}>
                          <LabelInput
                            label="Discount (%)"
                            type="number"
                            min={0}
                            max={100}
                            step={0.01}
                            value={localRentalGlobal?.discount?.discount ?? ""}
                            onChange={(e) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                discount: {
                                  ...prev.discount,
                                  discount: parseNumericInputWithValidation(
                                    "discount",
                                    e.target.value,
                                    prev.discount?.discount ?? 0,
                                    "localRentalGlobal.discount.discount"
                                  ),
                                },
                              }))
                            }
                            onBlur={(e) =>
                              handleDecimalBlur(
                                "discount",
                                e.target.value,
                                "localRentalGlobal.discount.discount",
                                (nextValue) =>
                                  setLocalRentalGlobal((prev) => ({
                                    ...prev,
                                    discount: {
                                      ...prev.discount,
                                      discount: nextValue,
                                    },
                                  }))
                              )
                            }
                          />
                          {decimalValidationErrors["localRentalGlobal.discount.discount"] && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                              {decimalValidationErrors["localRentalGlobal.discount.discount"]}
                            </Text>
                          )}

                          <DateTimeRange
                            value={localRentalGlobal?.discount || {}}
                            errors={localRentalPricingErrors.discount}
                            isInvalid={hasAnyDateTimeError(localRentalPricingErrors.discount)}
                            onChange={(field, value) =>
                              updateLocalRentalGlobalWithValidation("discount", field, value)
                            }
                          />
                        </Grid>
                      )}

                      <Flex align="center" gap={2} mt={4}>
                        <Switch
                          isChecked={Boolean(localRentalGlobal?.surcharge?.isSurchargeApply)}
                          onChange={(e) => {
                            const checked = e.target.checked;

                            if (!checked) {
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                surcharge: {
                                  ...prev.surcharge,
                                  isSurchargeApply: false,
                                  amount: 0,
                                  startDate: "",
                                  startTime: "",
                                  endDate: "",
                                  endTime: "",
                                },
                              }));

                              setLocalRentalPricingErrors((prev) => ({
                                ...prev,
                                surcharge: createEmptyDateTimeErrors(),
                              }));
                            } else {
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                surcharge: {
                                  ...prev.surcharge,
                                  isSurchargeApply: true,
                                  startDate: getCurrentDate(),
                                  startTime: getCurrentTime(),
                                  endDate: "",
                                  endTime: "",
                                },
                              }));
                            }
                          }}
                        />
                        <Text>Apply Surcharge</Text>
                      </Flex>

                      {localRentalGlobal?.surcharge?.isSurchargeApply && (
                        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4} mt={3}>
                          <LabelInput
                            label="Surcharge Amount (₹)"
                            type="number"
                            min={0}
                            step={0.01}
                            value={localRentalGlobal?.surcharge?.amount ?? ""}
                            onChange={(e) =>
                              setLocalRentalGlobal((prev) => ({
                                ...prev,
                                surcharge: {
                                  ...prev.surcharge,
                                  amount: parseNumericInputWithValidation(
                                    "amount",
                                    e.target.value,
                                    prev.surcharge?.amount ?? 0,
                                    "localRentalGlobal.surcharge.amount"
                                  ),
                                },
                              }))
                            }
                            onBlur={(e) =>
                              handleDecimalBlur(
                                "amount",
                                e.target.value,
                                "localRentalGlobal.surcharge.amount",
                                (nextValue) =>
                                  setLocalRentalGlobal((prev) => ({
                                    ...prev,
                                    surcharge: {
                                      ...prev.surcharge,
                                      amount: nextValue,
                                    },
                                  }))
                              )
                            }
                          />
                          {decimalValidationErrors["localRentalGlobal.surcharge.amount"] && (
                            <Text color="red.500" fontSize="xs" mt={1}>
                              {decimalValidationErrors["localRentalGlobal.surcharge.amount"]}
                            </Text>
                          )}

                          <DateTimeRange
                            value={localRentalGlobal?.surcharge || {}}
                            errors={localRentalPricingErrors.surcharge}
                            isInvalid={hasAnyDateTimeError(localRentalPricingErrors.surcharge)}
                            onChange={(field, value) =>
                              updateLocalRentalGlobalWithValidation("surcharge", field, value)
                            }
                          />
                        </Grid>
                      )}
                    </Box>

                    <Flex justifyContent={"flex-end"} alignItems={"center"} mb={6}>
                      <Button onClick={handleAddLocalRental} borderRadius={5} px={6} fontWeight={500} py={2}>
                        Add Local Rental
                      </Button>
                    </Flex>

                    {localRentals?.map((rental, index) => (
                      <Accordion key={index} allowToggle w="100%" mb={4}>
                        <AccordionItem border="none">
                          {({ isExpanded: isRentalExpanded }) => (
                            <Box
                              border="1px solid #CBD5E0"
                              borderRadius="md"
                              w={isRentalExpanded ? "100%" : "100%"}
                              transition="height width 0.3s ease"
                            >
                              <AccordionButton w={"100%"}>
                                <Box
                                  flex="1"
                                  textAlign="left"
                                  fontSize="1rem !important"
                                  color="#1A202C !important"
                                >
                                  Local Rental {index + 1}
                                </Box>
                                <AccordionIcon />
                              </AccordionButton>
                              <AccordionPanel>
                                <Grid templateColumns={{ base: "1fr", md: "repeat(4, 1fr)" }} gap={4}>
                                  <LabelInput
                                    placeholder=" Enter Duration (hours)"
                                    label="Duration"
                                    name="durationHours"
                                    type={"number"}
                                    min={0}
                                    step={1}
                                    value={rental.durationHours || 0}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "durationHours",
                                        Number(e.target.value)
                                      )
                                    }
                                  />
                                  <LabelInput
                                    placeholder="Enter Included Km"
                                    label="Included Km"
                                    name="includedKm"
                                    type={"number"}
                                    min={0}
                                    step={1}
                                    value={rental.includedKm || 0}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "includedKm",
                                        Number(e.target.value)
                                      )
                                    }
                                  />
                                  <LabelInput
                                    placeholder="Enter Price"
                                    label="Price"
                                    name="price"
                                    type={"number"}
                                    min={0}
                                    step={0.01}
                                    value={rental.price ?? ""}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "price",
                                        parseNumericInputWithValidation(
                                          "price",
                                          e.target.value,
                                          rental.price || 0,
                                          `localRental.${index}.price`
                                        )
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleDecimalBlur(
                                        "price",
                                        e.target.value,
                                        `localRental.${index}.price`,
                                        (nextValue) =>
                                          handleLocalRentalChange(index, "price", nextValue)
                                      )
                                    }
                                  />
                                  {decimalValidationErrors[`localRental.${index}.price`] && (
                                    <Text color="red.500" fontSize="xs" mt={1}>
                                      {decimalValidationErrors[`localRental.${index}.price`]}
                                    </Text>
                                  )}

                                  <LabelInput
                                    placeholder="Enter Extra Km Price"
                                    label="Extra Km Price"
                                    name="extraKmRate"
                                    type={"number"}
                                    min={0}
                                    step={0.01}
                                    value={rental.extraKmRate ?? ""}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "extraKmRate",
                                        parseNumericInputWithValidation(
                                          "extraKmRate",
                                          e.target.value,
                                          rental.extraKmRate || 0,
                                          `localRental.${index}.extraKmRate`
                                        )
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleDecimalBlur(
                                        "extraKmRate",
                                        e.target.value,
                                        `localRental.${index}.extraKmRate`,
                                        (nextValue) =>
                                          handleLocalRentalChange(index, "extraKmRate", nextValue)
                                      )
                                    }
                                  />
                                  {decimalValidationErrors[`localRental.${index}.extraKmRate`] && (
                                    <Text color="red.500" fontSize="xs" mt={1}>
                                      {decimalValidationErrors[`localRental.${index}.extraKmRate`]}
                                    </Text>
                                  )}

                                  <LabelInput
                                    placeholder="Enter Extra Hour Price"
                                    label="Extra Hour Price"
                                    name="extraHourRate"
                                    type={"number"}
                                    min={0}
                                    step={0.01}
                                    value={rental.extraHourRate ?? ""}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "extraHourRate",
                                        parseNumericInputWithValidation(
                                          "extraHourRate",
                                          e.target.value,
                                          rental.extraHourRate || 0,
                                          `localRental.${index}.extraHourRate`
                                        )
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleDecimalBlur(
                                        "extraHourRate",
                                        e.target.value,
                                        `localRental.${index}.extraHourRate`,
                                        (nextValue) =>
                                          handleLocalRentalChange(index, "extraHourRate", nextValue)
                                      )
                                    }
                                  />
                                  {decimalValidationErrors[`localRental.${index}.extraHourRate`] && (
                                    <Text color="red.500" fontSize="xs" mt={1}>
                                      {decimalValidationErrors[`localRental.${index}.extraHourRate`]}
                                    </Text>
                                  )}
                                  <LabelInput
                                    label="Commission (%)"
                                    placeholder="Enter Commission %"
                                    type="number"
                                    min={0}
                                    max={100}
                                    step={0.01}
                                    value={rental.commissionPercent ?? ""}
                                    onChange={(e) =>
                                      handleLocalRentalChange(
                                        index,
                                        "commissionPercent",
                                        parseNumericInputWithValidation(
                                          "commissionPercent",
                                          e.target.value,
                                          rental.commissionPercent || 0,
                                          `localRental.${index}.commissionPercent`
                                        )
                                      )
                                    }
                                    onBlur={(e) =>
                                      handleDecimalBlur(
                                        "commissionPercent",
                                        e.target.value,
                                        `localRental.${index}.commissionPercent`,
                                        (nextValue) =>
                                          handleLocalRentalChange(index, "commissionPercent", nextValue)
                                      )
                                    }
                                  />
                                  {decimalValidationErrors[`localRental.${index}.commissionPercent`] && (
                                    <Text color="red.500" fontSize="xs" mt={1}>
                                      {decimalValidationErrors[`localRental.${index}.commissionPercent`]}
                                    </Text>
                                  )}
                                </Grid>

                                <Button mt={4} onClick={() => handleRemoveLocalRental(index)} borderRadius={0}>
                                  Remove
                                </Button>
                              </AccordionPanel>
                            </Box>
                          )}
                        </AccordionItem>
                      </Accordion>
                    ))}
                  </AccordionPanel>
                </Box>
              )}
            </AccordionItem>
          </Accordion>
        </Box>

        <Box px={6} py={6} borderRadius={"lg"} bg={"white"} my={"2rem"}>

          {/* Terms and conditions section */}
          <Flex justifyContent={"space-between"} mb={6}>
            <Heading fontSize="lg" mb={1} color={"#1A202C"}>
              Terms and Conditions
            </Heading>
            <Text color={"#90A3BF"} fontSize={"0.875rem"} letterSpacing={0}>Step 3 of 4</Text>
          </Flex>
          <Grid templateColumns={{ base: "1fr" }} gap={4}>
            <GridItem py={1}>
              <VStack align="stretch" spacing={3}>
                {terms.map((term, index) => (
                  <Flex key={term.id} align="flex-end" gap={3}>
                    <Box flex="1">
                      <LabelInput
                        placeholder="Enter term"
                        value={term.text}
                        onChange={(e) => {
                          const v = e.target.value;
                          setTerms(prev =>
                            prev.map(t => t.id === term.id ? { ...t, text: v } : t)
                          );
                        }}
                      />
                    </Box>

                    {terms.length > 1 && (
                      <IconButton
                        aria-label="Remove term"
                        icon={<IoIosCloseCircle />}
                        variant="ghost"
                        onClick={() =>
                          setTerms(prev => prev.filter(t => t.id !== term.id))
                        }
                      />
                    )}
                  </Flex>
                ))}
                {errors.terms && (
                  <Text color="red.500">{errors.terms}</Text>
                )}
              </VStack>
              <Flex w="100%" justifyContent="flex-end" mt={4}>
                <Button
                  onClick={() => {
                    setTerms(prev => {
                      const last = prev[prev.length - 1];
                      if (!last.text.trim()) {
                        toast({
                          title: "Empty term",
                          description: "Please fill the previous term before adding a new one.",
                          status: "warning",
                          duration: 3000,
                          isClosable: true,
                          position: "top",
                        });
                        return prev;
                      }

                      return [
                        ...prev,
                        { id: crypto.randomUUID(), text: "" }
                      ];
                    });
                  }}
                >
                  Add Point
                </Button>
              </Flex>
            </GridItem>
          </Grid>
        </Box>

        <Box px={6} py={6} borderRadius={"lg"} bg={"white"} my={"2rem"}>
          {/* Availability and Allocation Section */}
          <Flex justifyContent={"space-between"} mb={6}>
            <Heading fontSize="lg" mb={1} color={"#1A202C"}>
              Availability And Allocation
            </Heading>
            <Text color={"#90A3BF"} fontSize={"0.875rem"} letterSpacing={0}>Step 4 of 4</Text>
          </Flex>
          <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }} gap={4}>
            <GridItem py={3}>
              <Select
                variant={"filled"}
                name="status"
                borderRadius="lg"
                onChange={handleInputChange}
                // required
                value={data?.status || ""}
                w={"30%"}
              >
                <option value="" disabled>
                  Status
                </option>
                <option value="active">Active</option>
                <option value="discontinued">Discontinued</option>
                <option value="inactive">Inactive</option>
                <option value="underReview">Under Review</option>
              </Select>
              {errors.status && (
                <Text color="red.500" fontSize="xs">
                  {errors.status}
                </Text>
              )}
            </GridItem>
          </Grid>
        </Box>
        <Grid templateColumns={{
          base: "1fr",
          md: "repeat(2, 1fr)",
        }}
          gap={4}
          mb={6}>
          <GridItem>
            {/* Image Upload Section with Preview */}
            {preview ? (
              <Box px={6} py={6} borderRadius={"lg"} bg={"white"} my={"2rem"}>
                <Box
                  position="relative"
                  w="260px"
                  h="120px"
                  bg="orange.100"
                  mb={6}
                  borderRadius="md"
                >
                  <Image
                    src={preview || ""}
                    alt="Preview"
                    w="100%"
                    h="100%"
                    objectFit="cover"
                    borderRadius="md"
                  />
                  <IconButton
                    icon={<IoIosCloseCircle />}
                    size="sm"
                    position="absolute"
                    top="4px"
                    right="4px"
                    aria-label="Remove Image"
                    onClick={handleRemovePreview}
                    bg="white"
                    color="black"
                    borderRadius="md"
                    _hover={{ bg: "gray.100" }}
                  />
                </Box>
              </Box>
            ) : (
              <Flex
                as="label"
                align="center"
                justify="center"
                w="260px"
                h="120px"
                border="2px solid orange"
                bg="orange.100"
                color="orange.500"
                mb={6}
                cursor="pointer"
                borderRadius="md"
              >
                <VStack>
                  <FaRegImage />
                  <Text fontSize="sm">Upload Car Image</Text>
                </VStack>
                <Input
                  type="file"
                  accept="image/*"
                  display="none"
                  name="carImage"
                  onChange={handleFileChange}
                />
              </Flex>
            )}
          </GridItem>
          <GridItem >
            {/* Save and Cancel Buttons */}
            <Flex h={"100%"} justifyContent="flex-end" alignItems={"flex-end"} gap={4}>
              <Button
                variant="outline"
                borderColor="#396AFF"
                borderWidth={2}
                color="#396AFF"
                size={"sm"}
                bg="white"
                _hover={{
                  border: "none",
                  bg: "grey",
                  color: "black"
                }}
                borderRadius={5}
                px={10}
                fontWeight={600}
                py={5}
                w={"20%"}
                onClick={onCancel}
              >
                Cancel
              </Button>

              <Button type="submit" colorScheme="blue" borderColor="#396AFF"
                borderWidth={2} size={"sm"} borderRadius={5}
                px={10}
                fontWeight={600}
                isDisabled={isSaving || !isSaveEnabled}
                opacity={isSaveEnabled ? 1 : 0.5}
                w={"20%"}
                py={5}
              >
                {isSaving ? "Saving..." : "Save"}
              </Button>
            </Flex>
          </GridItem>
        </Grid>
      </form >
    </Box >
  );
};

export default Form;
